import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderAsync } from 'docx-preview';
import { Issue, IssueWithModifications } from '../../types';
import ClauseHighlighter from './ClauseHighlighter';
import { supabase } from '../../services/supabase';

// Import required CSS for react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../styles/split-screen.css';
import '../../styles/docx-viewer.css';

// Set up PDF.js worker - using unpkg as it's most reliable
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentViewerProps {
  documentId?: string;
  documentUrl?: string;
  documentType?: 'pdf' | 'docx' | 'txt';
  className?: string;
  onPageChange?: (pageNumber: number) => void;
  highlightedIssue?: IssueWithModifications | null;
  onHighlightComplete?: (success: boolean) => void;
  showPageNavigation?: boolean;
  enableTextSelection?: boolean;
}

interface DocumentViewerState {
  numPages: number | null;
  currentPage: number;
  loading: boolean;
  error: string | null;
  scale: number;
  documentData: string | Blob | null;
  textContent: string | null;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  documentUrl,
  documentType = 'pdf',
  className = '',
  onPageChange,
  highlightedIssue,
  onHighlightComplete,
  showPageNavigation = true,
  enableTextSelection = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<DocumentViewerState>({
    numPages: null,
    currentPage: 1,
    loading: true,
    error: null,
    scale: 1.0,
    documentData: null,
    textContent: null
  });

  // Load document data
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const loadDocument = async () => {
      if (!documentUrl && (!documentId || documentId.trim() === '')) {
        if (isMounted) {
          setState(prev => ({
            ...prev,
            error: 'No valid document URL or ID provided',
            loading: false
          }));
        }
        return;
      }

      try {
        if (isMounted) {
          setState(prev => ({ ...prev, loading: true, error: null }));
        }

        let finalUrl = documentUrl;
        if (!finalUrl && documentId && documentId.trim() !== '') {
          // Construct full URL from document ID with backend base URL
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          finalUrl = `${apiUrl}/api/v1/documents/${documentId}/download`;
        }

        if (!finalUrl) {
          throw new Error('Unable to determine document URL');
        }

        console.log('Loading document from URL:', finalUrl);

        // For PDF documents, fetch as blob to handle authentication properly
        if (documentType === 'pdf') {
          let token = (await supabase.auth.getSession()).data.session?.access_token || null;
          const headers: Record<string, string> = {
            'ngrok-skip-browser-warning': 'true' // Skip ngrok browser warning page
          };
          if (token) {
            // Clean up token - remove extra quotes if present
            token = token.replace(/^"(.*)"$/, '$1');
            headers['Authorization'] = `Bearer ${token}`;
          }

          console.log('Fetching PDF from:', finalUrl, 'with headers:', headers);
          const response = await fetch(finalUrl, { headers });
          
          console.log('Response status:', response.status, response.statusText);
          console.log('Response headers:', Object.fromEntries(response.headers.entries()));
          
          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Authentication required. Please log in again.');
            } else if (response.status === 404) {
              throw new Error('PDF document not found.');
            }
            throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
          }

          const pdfBlob = await response.blob();
          console.log('PDF blob size:', pdfBlob.size, 'type:', pdfBlob.type);
          
          // Check if we actually got a PDF or a JSON error response
          if (pdfBlob.type === 'application/json' || pdfBlob.size < 1000) {
            // Likely a JSON error response, not a PDF
            const textContent = await pdfBlob.text();
            console.log('JSON error response received:', textContent);
            
            try {
              const errorData = JSON.parse(textContent);
              if (errorData.error) {
                throw new Error(errorData.error.message || 'Authentication failed');
              } else {
                throw new Error(`Server returned JSON instead of PDF: ${textContent}`);
              }
            } catch (parseError) {
              throw new Error(`Server returned non-PDF content: ${textContent.substring(0, 200)}...`);
            }
          }
          
          // Check if we actually got a PDF by examining the first few bytes
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const header = Array.from(uint8Array.slice(0, 10)).map(b => String.fromCharCode(b)).join('');
          console.log('File header (first 10 bytes):', header);
          console.log('File header (hex):', Array.from(uint8Array.slice(0, 10)).map(b => b.toString(16).padStart(2, '0')).join(' '));
          
          // Check if it's actually a PDF (should start with %PDF)
          if (!header.startsWith('%PDF')) {
            // It's not a PDF, let's see what it is
            const textContent = new TextDecoder().decode(uint8Array.slice(0, 500));
            console.log('Non-PDF content received:', textContent);
            throw new Error(`Server returned non-PDF content. File header: ${header}, Content: ${textContent.substring(0, 100)}...`);
          }
          
          // Convert back to blob for react-pdf
          const validPdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });
          
          if (isMounted) {
            setState(prev => ({ 
              ...prev, 
              documentData: validPdfBlob,
              loading: false 
            }));
          }
        } else if (documentType === 'docx') {
          // For DOCX files, fetch the raw file for client-side rendering with docx-preview
          let token = (await supabase.auth.getSession()).data.session?.access_token || null;
          const headers: Record<string, string> = {
            'ngrok-skip-browser-warning': 'true' // Skip ngrok browser warning page
          };
          if (token) {
            // Clean up token - remove extra quotes if present
            token = token.replace(/^"(.*)"$/, '$1');
            headers['Authorization'] = `Bearer ${token}`;
          }

          // Fetch raw DOCX file (not extracted text)
          const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
          const rawDocxUrl = `${apiUrl}/api/v1/documents/${documentId}/download/raw`;

          console.log('Fetching raw DOCX file from:', rawDocxUrl);
          console.log('Auth token present:', !!token);
          const response = await fetch(rawDocxUrl, { headers });

          console.log('Response status:', response.status, response.statusText);
          console.log('Response Content-Type:', response.headers.get('Content-Type'));

          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Authentication required. Please log in again.');
            } else if (response.status === 404) {
              throw new Error('DOCX document not found.');
            }
            throw new Error(`Failed to load DOCX: ${response.status} ${response.statusText}`);
          }

          // Get DOCX as blob
          const docxBlob = await response.blob();
          console.log('DOCX blob size:', docxBlob.size, 'type:', docxBlob.type);

          // Check if we got HTML instead of DOCX (common error case)
          if (docxBlob.type === 'text/html' || docxBlob.type.includes('html')) {
            const htmlContent = await docxBlob.text();
            console.error('Received HTML instead of DOCX:', htmlContent.substring(0, 500));
            throw new Error('Server returned an error page instead of the document. This may indicate a routing or authentication issue.');
          }

          // Verify it's a valid DOCX file (ZIP signature: PK)
          const arrayBuffer = await docxBlob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const headerBytes = Array.from(uint8Array.slice(0, 2));
          const header = String.fromCharCode(...headerBytes);

          if (header !== 'PK') {
            // Log first 100 bytes to debug what we actually received
            const headerPreview = new TextDecoder().decode(uint8Array.slice(0, 100));
            console.error('Invalid DOCX header. First 100 bytes:', headerPreview);
            throw new Error('Invalid DOCX file format - file does not appear to be a valid DOCX document');
          }

          // Store the blob for rendering
          if (isMounted) {
            setState(prev => ({
              ...prev,
              documentData: new Blob([arrayBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              }),
              loading: false
            }));
          }
        } else {
          // For TXT and other document types, get extracted text content
          let token = (await supabase.auth.getSession()).data.session?.access_token || null;
          const headers: Record<string, string> = {
            'ngrok-skip-browser-warning': 'true' // Skip ngrok browser warning page
          };
          if (token) {
            // Clean up token - remove extra quotes if present
            token = token.replace(/^"(.*)"$/, '$1');
            headers['Authorization'] = `Bearer ${token}`;
          }

          // Use text extraction endpoint for TXT files
          let textUrl = finalUrl;
          if (documentId && documentId.trim() !== '') {
            const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
            textUrl = `${apiUrl}/api/v1/documents/${documentId}/text`;
          }

          console.log('Fetching document content from:', textUrl);
          console.log('Document ID:', documentId, 'Document Type:', documentType);
          const response = await fetch(textUrl, { headers });
          
          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Authentication required. Please log in again.');
            } else if (response.status === 404) {
              throw new Error('Document not found.');
            }
            throw new Error(`Failed to load document: ${response.statusText}`);
          }

          // For TXT files, use direct text response
          const content = await response.text();
          if (isMounted) {
            setState(prev => ({
              ...prev,
              documentData: textUrl || null,
              textContent: content,
              loading: false
            }));
          }
        }
      } catch (error: any) {
        console.error('Failed to load document:', error);
        if (isMounted) {
          setState(prev => ({
            ...prev,
            error: error.message || 'Failed to load document',
            loading: false
          }));
        }
      }
    };

    loadDocument();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [documentUrl, documentId, documentType]);

  // Handle page navigation
  const goToPage = useCallback((pageNumber: number) => {
    if (pageNumber >= 1 && (!state.numPages || pageNumber <= state.numPages)) {
      setState(prev => ({ ...prev, currentPage: pageNumber }));
      onPageChange?.(pageNumber);
    }
  }, [state.numPages, onPageChange]);

  // Scroll to specific page in continuous view
  const scrollToPage = useCallback((pageNumber: number) => {
    console.log('DocumentViewer: Attempting to scroll to page:', pageNumber, 'Total pages:', state.numPages);
    
    if (documentType !== 'pdf') {
      console.log('DocumentViewer: Not a PDF, skipping page scroll');
      return;
    }
    
    if (pageNumber < 1 || (state.numPages && pageNumber > state.numPages)) {
      console.log('DocumentViewer: Invalid page number');
      return;
    }
    
    // Ensure the page is visible before scrolling
    setVisiblePages(prev => {
      const newSet = new Set(prev);
      newSet.add(pageNumber);
      
      // Also add adjacent pages for better loading
      if (pageNumber > 1) newSet.add(pageNumber - 1);
      if (pageNumber < (state.numPages || 999)) newSet.add(pageNumber + 1);
      
      return newSet;
    });
    
    // Wait a moment for the page to render, then scroll
    setTimeout(() => {
      const pageElement = containerRef.current?.querySelector(`[data-page-number="${pageNumber}"]`);
      console.log('DocumentViewer: Found page element:', !!pageElement);
      
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight the page temporarily
        const originalBg = (pageElement as HTMLElement).style.backgroundColor;
        (pageElement as HTMLElement).style.backgroundColor = 'rgba(255, 235, 59, 0.2)';
        (pageElement as HTMLElement).style.border = '2px solid #fbc02d';
        
        setTimeout(() => {
          (pageElement as HTMLElement).style.backgroundColor = originalBg;
          (pageElement as HTMLElement).style.border = '';
        }, 3000);
        
        onHighlightComplete?.(true);
      } else {
        console.warn('DocumentViewer: Could not find page element for page', pageNumber);
        onHighlightComplete?.(false);
      }
    }, 200); // Increased delay to allow page rendering
  }, [documentType, state.numPages, onHighlightComplete]);

  // Auto-scroll to text for both PDF and non-PDF documents
  // Improved text search using techniques from ClauseHighlighter
  const scrollToText = useCallback((searchText: string, useGlobalSearch: boolean = true) => {
    if (!searchText || !containerRef.current) return;

    console.log('DocumentViewer: Attempting to scroll to text:', searchText.substring(0, 100));
    console.log('DocumentViewer: Global search enabled:', useGlobalSearch);

    try {
      const container = containerRef.current;

      // Try browser's native find API first (most reliable for all document types)
      // CRITICAL FIX: Only use window.find() when we have reliable search text (excerpt or chunk_text)
      // NOT when searching with title-extracted phrases which may match UI text instead of document
      if (useGlobalSearch && (window as any).find && searchText.length >= 20) {
        console.log('DocumentViewer: Using browser find API');
        const found = (window as any).find(searchText.substring(0, 50));
        if (found) {
          onHighlightComplete?.(true);
          return;
        }
      }

      if (documentType === 'pdf') {
        // For PDFs, search in the text layer elements
        const textElements = container.querySelectorAll('.react-pdf__Page__textContent span, .react-pdf__Page__textContent div');
        console.log('DocumentViewer: Found', textElements.length, 'PDF text elements');

        // Try progressively shorter search fragments for better matching
        const searchFragments = [
          searchText.substring(0, 50),
          searchText.substring(0, 30),
          searchText.substring(0, 20)
        ];

        let found = false;
        for (const fragment of searchFragments) {
          if (found) break;

          for (let i = 0; i < textElements.length; i++) {
            const element = textElements[i];
            const elementText = element.textContent || '';

            if (elementText.toLowerCase().includes(fragment.toLowerCase()) ||
                fragment.toLowerCase().includes(elementText.toLowerCase().trim())) {

              console.log('DocumentViewer: Found matching PDF text element, scrolling');

              // Find the parent page wrapper
              const pageWrapper = element.closest('.pdf-page-wrapper');
              if (pageWrapper) {
                pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Highlight the page temporarily
                const originalBg = (pageWrapper as HTMLElement).style.backgroundColor;
                (pageWrapper as HTMLElement).style.backgroundColor = 'rgba(255, 235, 59, 0.2)';
                (pageWrapper as HTMLElement).style.border = '2px solid #fbc02d';

                setTimeout(() => {
                  (pageWrapper as HTMLElement).style.backgroundColor = originalBg;
                  (pageWrapper as HTMLElement).style.border = '';
                }, 3000);

                onHighlightComplete?.(true);
                found = true;
                break;
              }
            }
          }
        }

        if (!found) {
          console.warn('DocumentViewer: Could not find text in PDF');
          onHighlightComplete?.(false);
        }
      } else {
        // For non-PDF documents (DOCX, TXT) - use TreeWalker for comprehensive search
        console.log('DocumentViewer: Using TreeWalker for non-PDF search');
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT
        );

        let node;
        let found = false;
        // Try progressively shorter fragments
        const searchFragments = [searchText.substring(0, 50), searchText.substring(0, 30), searchText.substring(0, 20)];

        for (const fragment of searchFragments) {
          if (found) break;

          walker.currentNode = container;  // Reset walker
          while ((node = walker.nextNode()) && !found) {
            if (node.textContent && node.textContent.toLowerCase().includes(fragment.toLowerCase())) {
              const element = node.parentElement;
              if (element) {
                console.log('DocumentViewer: Found matching text element with TreeWalker, scrolling');
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Highlight the found text temporarily
                const originalBg = element.style.backgroundColor;
                element.style.backgroundColor = 'rgba(255, 235, 59, 0.3)';
                element.style.border = '2px solid #fbc02d';

                setTimeout(() => {
                  element.style.backgroundColor = originalBg;
                  element.style.border = '';
                }, 3000);

                onHighlightComplete?.(true);
                found = true;
                break;
              }
            }
          }
        }

        if (!found) {
          console.warn('DocumentViewer: Could not find text in document');
          onHighlightComplete?.(false);
        }
      }
    } catch (error) {
      console.warn('Text scroll failed:', error);
      onHighlightComplete?.(false);
    }
  }, [documentType, onHighlightComplete]);

  // Navigate to issue location in continuous scroll view
  useEffect(() => {
    if (!highlightedIssue) return;

    console.log('DocumentViewer: Highlighting issue:', highlightedIssue);
    console.log('DocumentViewer: Document type:', documentType);

    // IMPROVED: Get the search text with better prioritization
    // 1. Use AI-provided excerpt (most specific)
    // 2. Fall back to chunk_text (specific to document location)
    // 3. Extract key phrase from issue title (least reliable - avoid if possible)
    let searchText = highlightedIssue.original_ai_data?.document_excerpt;
    let searchSource: 'excerpt' | 'chunk_text' | 'title' = 'excerpt';

    if (!searchText) {
      // Try chunk_text next (most reliable after excerpt)
      searchText = highlightedIssue.chunk_text || undefined;
      searchSource = 'chunk_text';

      if (!searchText) {
        // Last resort: extract from issue title
        // NOTE: This is unreliable and may match UI text instead of document text
        const title = highlightedIssue.title || '';

        // Special case mappings for common legal terms that appear differently in documents
        const termMappings: Record<string, string> = {
          'Governing Law': 'governed by the laws',
          'Signatory Authority': 'signature',
          'Force Majeure': 'Force Majeure',
          'Indemnity': 'indemnif',
          'Termination': 'terminate',
          'Confidentiality': 'confidential',
          'Limitation of Liability': 'liability',
          'Intellectual Property': 'intellectual property'
        };

        // Extract key legal terms from title (remove common words)
        let keyPhraseMatch = title
          .replace(/^(Incomplete|Missing|Vague|Undefined|Unlimited|Weak|Ambiguous|Unclear)\s+/i, '')
          .replace(/\s+(Clause|Provision|Issue|Problem|Risk|Exposure)s?$/i, '')
          .trim();

        // Try to map to actual document text
        for (const [term, documentText] of Object.entries(termMappings)) {
          if (keyPhraseMatch.includes(term)) {
            keyPhraseMatch = documentText;
            break;
          }
        }

        if (keyPhraseMatch && keyPhraseMatch.length > 5) {
          searchText = keyPhraseMatch;
          searchSource = 'title';
          console.warn('DocumentViewer: Using key phrase from title (unreliable):', searchText);
        }
      }
    }

    console.log(`DocumentViewer: Search source: ${searchSource}, text:`, searchText?.substring(0, 100));

    // Handle missing clause issues (no text to highlight)
    // ONLY check issue_type, not missing data (chunk_text might be empty even for valid issues)
    const isMissingClauseIssue = highlightedIssue.issue_type === 'missing_clause';

    if (isMissingClauseIssue) {
      console.log('DocumentViewer: This is a missing clause issue - no specific location to highlight');
      // Notify completion as "success" since there's nothing to highlight
      onHighlightComplete?.(true);
      return;
    }

    if (documentType === 'pdf') {
      // For PDFs, try both page navigation and text search
      // FIXED: Use actual page_number from document_references (not calculated from chunk_index)
      const targetPage = highlightedIssue.document_references?.page_number;

      console.log('DocumentViewer: Target page for PDF:', targetPage);
      console.log('DocumentViewer: Search text for PDF:', searchText?.substring(0, 100));

      // Try page navigation first if we have a page number
      if (targetPage) {
        scrollToPage(targetPage);
      }

      // Also try text-based search for better precision (after a delay)
      if (searchText) {
        const text = searchText; // Type assertion for closure
        const useGlobal = searchSource !== 'title'; // Don't use global search for title-based searches
        setTimeout(() => {
          scrollToText(text, useGlobal);
        }, 300); // Small delay to allow page scroll to complete first
      } else if (!targetPage) {
        // No page number and no text - can't highlight
        console.warn('DocumentViewer: No page number or search text available for highlighting');
        onHighlightComplete?.(false);
      }
    } else {
      // For non-PDF documents, use text-based scrolling
      console.log('DocumentViewer: Search text for non-PDF:', searchText?.substring(0, 100));
      if (searchText) {
        const text = searchText; // Type assertion for closure
        const useGlobal = searchSource !== 'title'; // Don't use global search for title-based searches
        scrollToText(text, useGlobal);
      } else {
        console.warn('DocumentViewer: No search text available for highlighting');
        onHighlightComplete?.(false);
      }
    }
  }, [highlightedIssue, documentType, scrollToPage, scrollToText, onHighlightComplete]);

  // PDF document load success
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setState(prev => ({ ...prev, numPages, loading: false }));
  };

  // PDF document load error
  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setState(prev => ({
      ...prev,
      error: `Failed to load PDF: ${error.message}`,
      loading: false
    }));
  };

  // Zoom controls
  const zoomIn = () => {
    setState(prev => ({ ...prev, scale: Math.min(prev.scale + 0.2, 3.0) }));
  };

  const zoomOut = () => {
    setState(prev => ({ ...prev, scale: Math.max(prev.scale - 0.2, 0.5) }));
  };

  const resetZoom = () => {
    setState(prev => ({ ...prev, scale: 1.0 }));
  };

  // Virtual scrolling state for PDF pages
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1, 2, 3])); // Start with first 3 pages

  // Intersection observer for lazy loading pages
  const pageObserver = useRef<IntersectionObserver | null>(null);

  // Initialize intersection observer for virtual scrolling
  useEffect(() => {
    if (!containerRef.current || documentType !== 'pdf') return;

    pageObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNumber = parseInt(entry.target.getAttribute('data-page-number') || '0');
          if (entry.isIntersecting && pageNumber > 0) {
            setVisiblePages(prev => {
              const newSet = new Set(prev);
              newSet.add(pageNumber);
              return newSet;
            });
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: '200px', // Start loading pages 200px before they're visible
        threshold: 0.1
      }
    );

    return () => {
      if (pageObserver.current) {
        pageObserver.current.disconnect();
        pageObserver.current = null;
      }
    };
  }, [documentType, state.numPages]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pageObserver.current) {
        pageObserver.current.disconnect();
        pageObserver.current = null;
      }
      // Reset visible pages to prevent stale state
      setVisiblePages(new Set([1, 2, 3]));
    };
  }, []);

  // Render PDF content with contained scrolling
  const renderPDFContent = () => {
    if (!state.documentData) return null;

    return (
      <div className="pdf-document-container">
        <Document
          key={`${documentId}-${documentUrl}-${documentType}`} // Force remount when document changes
          file={state.documentData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading PDF...</span>
            </div>
          }
          error={
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="h-16 w-16 text-danger mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">PDF Load Failed</h3>
                <p className="text-gray-600 text-sm">Unable to load the PDF document. Please try refreshing the page.</p>
              </div>
            </div>
          }
        >
          {/* Contained scrolling PDF pages */}
          <div className="pdf-pages-container">
            {state.numPages && Array.from(new Array(state.numPages), (el, index) => {
              const pageNumber = index + 1;
              const shouldRender = visiblePages.has(pageNumber);
              
              return (
                <div 
                  key={`page_${pageNumber}`} 
                  className="pdf-page-wrapper"
                  data-page-number={pageNumber}
                  ref={(el) => {
                    if (el && pageObserver.current) {
                      pageObserver.current.observe(el);
                    }
                  }}
                >
                  {/* Page number indicator */}
                  <div className="text-center text-xs text-gray-500 mb-2 bg-gradient-to-r from-gray-100 to-gray-200 py-2 px-4 rounded-t-lg">
                    Page {pageNumber} of {state.numPages}
                  </div>
                  
                  {shouldRender ? (
                    <Page
                      pageNumber={pageNumber}
                      scale={state.scale}
                      renderTextLayer={enableTextSelection}
                      renderAnnotationLayer={false}
                      className="pdf-page shadow-lg"
                      loading={
                        <div className="flex items-center justify-center h-96 bg-gray-100 rounded">
                          <div className="flex flex-col items-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                            <div className="text-gray-500 text-sm">Loading page {pageNumber}...</div>
                          </div>
                        </div>
                      }
                      error={
                        <div className="flex items-center justify-center h-96 bg-danger-soft rounded border border-danger-soft">
                          <div className="text-danger text-sm">Error loading page {pageNumber}</div>
                        </div>
                      }
                    />
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-gray-50 rounded border-2 border-dashed border-gray-300">
                      <div className="text-gray-400 text-sm">Page {pageNumber} - Scroll to load</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Document>
      </div>
    );
  };

  // Render text-based content (DOCX, TXT) with enhanced formatting
  const renderTextContent = () => {
    if (!state.textContent) return null;

    // For TXT files only (DOCX now uses renderDOCXContent)
    return (
      <div className="text-content p-6 bg-surface rounded shadow-sm">
        <div className="prose max-w-none">
          <pre className="whitespace-pre-wrap font-sans text-gray-800 leading-relaxed text-sm">
            {state.textContent}
          </pre>
        </div>
      </div>
    );
  };

  // Render DOCX content with docx-preview library
  const renderDOCXContent = useCallback(() => {
    // Show loading state while document is being fetched
    if (state.loading) {
      return (
        <div className="docx-document-container" style={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading DOCX document...</p>
          </div>
        </div>
      );
    }

    // Show placeholder if no document data yet
    if (!state.documentData) {
      return (
        <div className="docx-document-container" style={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="text-center text-gray-500">
            <p>No document loaded</p>
          </div>
        </div>
      );
    }

    return (
      <div className="docx-document-container">
        <div
          ref={docxContainerRef}
          className="docx-content"
          style={{
            backgroundColor: 'white',
            padding: '16px', // Reduced from 32px for better space utilization
            minHeight: '100%',
            width: '100%', // Fill container width
            maxWidth: '100%', // Don't exceed container
            margin: '0',
            boxSizing: 'border-box',
            border: 'none', // Ensure no border
            boxShadow: 'none' // Ensure no shadow
          }}
        />
      </div>
    );
  }, [state.documentData, state.loading]);

  // Trigger DOCX rendering when document loads
  useEffect(() => {
    const renderDocx = async () => {
      console.log('DOCX rendering check:', {
        documentType,
        hasDocumentData: !!state.documentData,
        hasContainer: !!docxContainerRef.current,
        loading: state.loading
      });

      if (documentType === 'docx' && state.documentData && docxContainerRef.current && !state.loading) {
        try {
          console.log('Starting DOCX rendering...');

          // Clear container
          docxContainerRef.current.innerHTML = '';

          // Render DOCX with docx-preview
          await renderAsync(
            state.documentData as Blob,
            docxContainerRef.current,
            undefined, // Use default document part
            {
              className: 'docx-wrapper',
              inWrapper: false, // Remove gray wrapper/border around document
              ignoreWidth: false,
              ignoreHeight: false,
              ignoreFonts: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: false,
              experimental: false,
              trimXmlDeclaration: true,
              renderChanges: true, // Show tracked changes with proper ins/del styling
              renderComments: false, // Disabled — popover rendering is broken (overlaps text). Comments visible in Card view.
              debug: false
            }
          );

          console.log('DOCX rendering complete successfully');
        } catch (error) {
          console.error('DOCX rendering error:', error);
          setState(prev => ({
            ...prev,
            error: 'Failed to render DOCX document. The file may be corrupted.'
          }));
        }
      }
    };

    renderDocx();
  }, [documentType, state.documentData, state.loading]);

  // Render controls - only shown for PDF (has page count + zoom); DOCX/TXT skip to save space
  const renderControls = () => {
    if (documentType !== 'pdf') return null;

    return (
      <div className="document-controls bg-surface border-b border-gray-200 p-3 flex items-center justify-between">
        {/* Document Info */}
        {state.numPages && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {state.numPages} page{state.numPages > 1 ? 's' : ''} • Continuous scroll
            </span>
          </div>
        )}

        {/* Zoom Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={zoomOut}
            disabled={state.scale <= 0.5}
            className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
            title="Zoom out"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <span className="text-sm text-gray-600 px-2">
            {Math.round(state.scale * 100)}%
          </span>

          <button
            onClick={resetZoom}
            className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
            title="Reset zoom"
          >
            Reset
          </button>

          <button
            onClick={zoomIn}
            disabled={state.scale >= 3.0}
            className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"
            title="Zoom in"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Document Type Indicator */}
        <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          PDF
        </div>
      </div>
    );
  };

  if (state.loading) {
    return (
      <div className={`document-viewer ${className}`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`document-viewer ${className}`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <svg className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Document Load Error</h3>
            <p className="text-gray-600 mb-4">{state.error}</p>
            <button
              onClick={() => setState(prev => ({ ...prev, error: null, loading: true }))}
              className="bg-primary-action text-white px-4 py-2 rounded-md hover:bg-primary-action-hover"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`document-viewer flex flex-col h-full ${className}`}>
      {renderControls()}
      
      {/* Clause Highlighter Integration */}
      {highlightedIssue && documentId && documentId.trim() !== '' && (
        <div className="bg-yellow-50 border-b border-yellow-200 p-2"> {/* phase-2b: kept literal — highlighter pen identity */}
          <ClauseHighlighter
            documentId={documentId}
            clauseId={highlightedIssue.document_references?.clause_reference}
            text={highlightedIssue.chunk_text || undefined}
            onHighlightComplete={onHighlightComplete}
            showPageNavigation={false}
            enableScrollToText={documentType !== 'pdf'}
            className="text-sm"
          />
        </div>
      )}
      
      {/* Document Content */}
      <div className="flex-1 overflow-auto bg-gray-50 p-2">
        {documentType === 'pdf' ? renderPDFContent() :
         documentType === 'docx' ? renderDOCXContent() :
         renderTextContent()}
      </div>
    </div>
  );
};

export default DocumentViewer;